import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useRef, useState } from 'react'
import { codeOf } from '../lib/api-error.ts'
import { messageFor } from '../lib/messages.ts'
import { NotFoundScreen } from '../screens/not-found.tsx'
import { useDocumentTitle } from '../shell/document-title.ts'
import { Notice } from '../shell/notice.tsx'
import { fetchTenant, tenantQueryKey } from '../tenants/gateway.ts'
import { TenantHeader } from '../tenants/tenant-header.tsx'
import {
  CATALOGUE_QUERY_KEY,
  fetchFeatureFlagCatalogue,
  fetchTenantFlags,
  removeTenantFlag,
  saveTenantFlag,
  tenantFlagsQueryKey,
} from './gateway.ts'
import { RemoveFlagDialog } from './remove-flag-dialog.tsx'

/**
 * Feature flags — docs/ux/PLATFORM-ADMIN.md §14, against P04a-api's endpoints.
 *
 * **P04 shipped this route as an honest "not available yet" panel**, because none of the
 * three endpoints it needs existed. **P04a-api built them; this task (P04b) builds the
 * real screen against them**, replacing that placeholder in the same file:
 *
 * - One row per **catalogue** entry (`GET /platform/feature-flags`, platform-realm only —
 *   §14.3, §6.2), with a checkbox reflecting this tenant's own resolved value for that key.
 *   Toggling saves that one key alone (`PUT .../feature-flags/{key}`), never a batch.
 * - A separate section for a key the tenant's map carries that the catalogue does not list
 *   (§14.2) — its raw key, its value, and a Remove… control
 *   (`DELETE .../feature-flags/{key}`) **that opens a confirmation** (§19, P07): the key goes
 *   only when the confirm is pressed.
 * - **No free-text key field anywhere.** The only way to set a key is to check a row the
 *   catalogue already lists; the API does not enforce that itself
 *   (`docs/DECISIONS.md`, "P04a's per-tenant flag write does not check the key against the
 *   catalogue"), so this screen's enforcement is simply never rendering the control that
 *   would violate it.
 * - **No optimistic flip.** A checkbox moves only once its own write has succeeded; a
 *   failure leaves it exactly where it was, with a message at that row (§14.4 — "a checkbox
 *   that flips optimistically and silently reverts is worse than one that does not move").
 *
 * **Pending and error state is tracked per key, in local state, rather than through one
 * shared mutation object.** A catalogue with several rows lets an owner toggle more than
 * one before the first answers; a single `useMutation`'s `variables`/`isError` describe
 * only the most recently *started* call, so two rows in flight together would blur into
 * each other's pending and error state. Keying both by the flag key keeps each row's
 * outcome its own.
 */

export type FlagsScreenProps = {
  readonly tenantId: string
}

/**
 * **One screen per tenant id** — `fix-console-settings-form-keeps-other-tenant-values` (PR #249
 * review, S2). The router renders one instance for every tenant id, so a move straight from tenant
 * A's page to tenant B's (the browser's history menu) used to keep the per-key pending and failure
 * state below, which is keyed by flag name only: a row saving or failed on A showed as saving or
 * failed on B's row of the same name. The key starts the screen again for each tenant. A write
 * started on A still lands in A's cache entry, because `withPending` captured A's id. See
 * `docs/DECISIONS.md`, "Console screens that act on one tenant are keyed by tenant id".
 */
export function FlagsScreen({ tenantId }: FlagsScreenProps) {
  return <TenantFlags key={tenantId} tenantId={tenantId} />
}

function TenantFlags({ tenantId }: FlagsScreenProps) {
  const queryClient = useQueryClient()

  const tenant = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: () => fetchTenant(tenantId),
  })
  const catalogue = useQuery({
    queryKey: CATALOGUE_QUERY_KEY,
    queryFn: fetchFeatureFlagCatalogue,
  })
  const flags = useQuery({
    queryKey: tenantFlagsQueryKey(tenantId),
    queryFn: () => fetchTenantFlags(tenantId),
  })

  useDocumentTitle('Feature flags', tenant.data?.name, 'Tenants')
  const catalogueHeadingId = useId()
  const strayHeadingId = useId()

  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(new Set())
  const [rowErrors, setRowErrors] = useState<Readonly<Record<string, string>>>({})

  const withPending = async (key: string, run: () => Promise<Record<string, boolean>>) => {
    setPendingKeys((prev) => new Set(prev).add(key))
    setRowErrors(({ [key]: _dropped, ...rest }) => rest)
    try {
      const updated = await run()
      queryClient.setQueryData(tenantFlagsQueryKey(tenantId), updated)
    } catch (error) {
      setRowErrors((prev) => ({ ...prev, [key]: messageFor('flags', codeOf(error)) }))
    } finally {
      setPendingKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  // Set synchronously, because `pendingKeys` only changes on the next render: a second change to
  // the same switch in the same frame must not send a second request now that the switch stays
  // enabled while it saves.
  const toggleInFlight = useRef<Set<string>>(new Set())

  const toggleFlag = (key: string, enabled: boolean) => {
    if (toggleInFlight.current.has(key)) {
      return
    }
    toggleInFlight.current.add(key)
    void withPending(key, () => saveTenantFlag(tenantId, key, enabled)).finally(() => {
      toggleInFlight.current.delete(key)
    })
  }

  // The removal the owner is being asked to confirm, with the value the key held when the dialog
  // opened (§19 names it). A failure of the confirm is shown inside the dialog, not on the row.
  const [removing, setRemoving] = useState<{ key: string; value: boolean } | undefined>(undefined)
  const [removeError, setRemoveError] = useState<string | undefined>(undefined)
  // Set synchronously, because `pendingKeys` only changes on the next render: two presses in one
  // frame must send one request.
  const removeInFlight = useRef(false)

  const cancelRemove = () => {
    setRemoving(undefined)
    setRemoveError(undefined)
  }

  const confirmRemove = async () => {
    if (removing === undefined || removeInFlight.current) {
      return
    }
    removeInFlight.current = true
    const { key } = removing
    setRemoveError(undefined)
    setPendingKeys((prev) => new Set(prev).add(key))
    try {
      // `tenantId` is the one this screen was keyed with, so a removal begun here lands in this
      // tenant's cache entry even if the owner has since moved to another tenant's page.
      const updated = await removeTenantFlag(tenantId, key)
      queryClient.setQueryData(tenantFlagsQueryKey(tenantId), updated)
      setRemoving(undefined)
    } catch (error) {
      setRemoveError(messageFor('flags', codeOf(error)))
    } finally {
      removeInFlight.current = false
      setPendingKeys((prev) => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  if (tenant.isError) {
    const code = codeOf(tenant.error)
    if (code === 'not_found' || code === 'validation_failed') {
      // The router's own not-found page, not a copy of it (`NotFoundScreen`).
      return <NotFoundScreen />
    }
  }

  const retired = tenant.data?.state === 'retired'
  const loading = catalogue.isPending || flags.isPending
  const catalogueRows = catalogue.data ?? []
  const flagMap = flags.data ?? {}
  const catalogueKeys = new Set(catalogueRows.map((entry) => entry.key))
  const strayKeys = Object.keys(flagMap)
    .filter((key) => !catalogueKeys.has(key))
    .sort((a, b) => a.localeCompare(b))
  const emptyCatalogue = catalogue.isSuccess && catalogueRows.length === 0
  const onCount = catalogueRows.filter((entry) => flagMap[entry.key] === true).length

  return (
    <main className="page">
      <TenantHeader tenantId={tenantId} />
      <h2 className="visually-hidden">Feature flags</h2>

      {tenant.isError &&
      codeOf(tenant.error) !== 'not_found' &&
      codeOf(tenant.error) !== 'validation_failed' ? (
        <Notice tone="failure">{messageFor('flags', codeOf(tenant.error))}</Notice>
      ) : null}

      {retired ? (
        <Notice tone="warning">
          This tenant is retired. Its flags stay readable below, but every switch is read-only.
        </Notice>
      ) : null}

      {catalogue.isError ? (
        <div className="panel panel-error" data-testid="catalogue-error">
          <h2>The flag catalogue could not be loaded</h2>
          <Notice tone="failure">{messageFor('flags', codeOf(catalogue.error))}</Notice>
          <button
            type="button"
            className="button"
            onClick={() => {
              void catalogue.refetch()
            }}
          >
            Try again
          </button>
        </div>
      ) : null}

      {flags.isError ? (
        <div className="panel panel-error" data-testid="flags-error">
          <h2>This tenant’s flags could not be loaded</h2>
          <Notice tone="failure">{messageFor('flags', codeOf(flags.error))}</Notice>
          <button
            type="button"
            className="button"
            onClick={() => {
              void flags.refetch()
            }}
          >
            Try again
          </button>
        </div>
      ) : null}

      {catalogue.isError || flags.isError ? null : (
        <>
          {emptyCatalogue ? (
            <div className="panel" data-testid="catalogue-empty">
              <h2>This platform defines no flags yet</h2>
              <p>
                There is nothing to switch for any tenant until a flag is added to the catalogue.
              </p>
            </div>
          ) : null}

          {loading || catalogueRows.length > 0 ? (
            <section
              className="card"
              aria-labelledby={catalogueHeadingId}
              aria-busy={loading || undefined}
            >
              <div className="card-head">
                <div>
                  <h2 id={catalogueHeadingId}>Catalogue</h2>
                  <p className="muted">
                    Each switch saves on its own and takes effect on the tenant’s next request.
                    Nothing flips until the save lands.
                  </p>
                </div>
                {loading ? null : (
                  <span className="small muted">
                    {onCount} of {catalogueRows.length} on
                  </span>
                )}
              </div>
              <div className="table-wrap">
                <table className="table table-stack">
                  <caption className="visually-hidden">
                    The platform’s feature-flag catalogue, and this tenant’s value for each key
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Flag</th>
                      <th scope="col">What it does</th>
                      <th scope="col">For this tenant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 2 }, (_unused, index) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
                          <tr key={`placeholder-${index}`}>
                            <td colSpan={3}>
                              <span className="skeleton" />
                            </td>
                          </tr>
                        ))
                      : catalogueRows.map((entry) => (
                          <CatalogueRow
                            key={entry.key}
                            entryKey={entry.key}
                            label={entry.label}
                            description={entry.description}
                            checked={flagMap[entry.key] === true}
                            disabled={retired}
                            pending={pendingKeys.has(entry.key)}
                            errorMessage={rowErrors[entry.key]}
                            onToggle={(enabled) => {
                              toggleFlag(entry.key, enabled)
                            }}
                          />
                        ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {!loading && strayKeys.length > 0 ? (
            <section className="card" aria-labelledby={strayHeadingId}>
              <div className="card-head">
                <div>
                  <h2 id={strayHeadingId}>Keys not in the catalogue</h2>
                  <p className="muted">
                    Set on this tenant, but the catalogue no longer describes them — a flag that was
                    renamed, or one written some other way. They still resolve for this tenant
                    exactly as shown; removing one is a data change, not a hide.
                  </p>
                </div>
              </div>
              <div className="table-wrap">
                <table className="table table-stack">
                  <caption className="visually-hidden">
                    Flag keys set on this tenant that are not in the platform catalogue
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Key</th>
                      <th scope="col">Value</th>
                      <th scope="col">
                        <span className="visually-hidden">Action</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {strayKeys.map((key) => (
                      <StrayRow
                        key={key}
                        entryKey={key}
                        value={flagMap[key] === true}
                        disabled={retired}
                        pending={pendingKeys.has(key)}
                        onRemove={() => {
                          setRemoveError(undefined)
                          setRemoving({ key, value: flagMap[key] === true })
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </>
      )}

      {removing === undefined ? null : (
        <RemoveFlagDialog
          flagKey={removing.key}
          value={removing.value}
          pending={pendingKeys.has(removing.key)}
          error={removeError}
          onCancel={cancelRemove}
          onConfirm={() => {
            void confirmRemove()
          }}
        />
      )}
    </main>
  )
}

function CatalogueRow({
  entryKey,
  label,
  description,
  checked,
  disabled,
  pending,
  errorMessage,
  onToggle,
}: {
  readonly entryKey: string
  readonly label: string
  readonly description: string
  readonly checked: boolean
  readonly disabled: boolean
  readonly pending: boolean
  readonly errorMessage: string | undefined
  readonly onToggle: (enabled: boolean) => void
}) {
  const nameId = useId()
  const stateId = useId()

  return (
    <tr>
      <td>
        <div className="strong" id={nameId}>
          {label}
        </div>
        <code className="tiny muted">{entryKey}</code>
      </td>
      <td className="secondary" data-label="What it does">
        {description}
      </td>
      <td data-label="For this tenant">
        {/* A real checkbox, named by the flag and its state word; only its drawing is custom. */}
        <label className={checked ? 'switch on' : 'switch'}>
          <input
            type="checkbox"
            checked={checked}
            // **While its own write is in flight the switch stays enabled for the browser** and is
            // only *announced* as unavailable (`aria-disabled`), with the change ignored below. A
            // natively disabled control cannot hold focus: the browser drops it to `<body>`, and
            // nothing gives it back when the write lands or fails, so the operator who pressed Space
            // lost their place (P07 QA D1; PLATFORM-ADMIN §14.1, §21). A retired tenant's switch is
            // different: it is never operable, so it stays natively disabled.
            disabled={disabled}
            aria-disabled={pending || undefined}
            aria-labelledby={`${nameId} ${stateId}`}
            onChange={(event) => {
              if (pending) {
                // React puts the controlled value back, so nothing flips while the write is out.
                return
              }
              onToggle(event.target.checked)
            }}
          />
          <span className="track" aria-hidden="true" />
          {pending ? (
            <span className="muted" id={stateId}>
              Saving…
            </span>
          ) : checked ? (
            <strong id={stateId}>On</strong>
          ) : (
            <span id={stateId}>Off</span>
          )}
        </label>
        {errorMessage === undefined ? null : (
          <Notice tone="failure" assertive>
            {errorMessage}
          </Notice>
        )}
      </td>
    </tr>
  )
}

function StrayRow({
  entryKey,
  value,
  disabled,
  pending,
  onRemove,
}: {
  readonly entryKey: string
  readonly value: boolean
  readonly disabled: boolean
  readonly pending: boolean
  readonly onRemove: () => void
}) {
  return (
    <tr>
      <td>
        <code>{entryKey}</code>
      </td>
      <td data-label="Value">
        <code>{value ? 'true' : 'false'}</code>
      </td>
      <td className="actions">
        <button
          type="button"
          className="button button-sm button-outline-danger"
          aria-label={`Remove ${entryKey}…`}
          disabled={disabled || pending}
          onClick={onRemove}
        >
          Remove…
        </button>
      </td>
    </tr>
  )
}
